import {
    useSetPrimaryImage,
    useSetSecondaryImage,
} from '~/hooks/image/useSelectedImages';
import { RecoilValue, useRecoilCallback, useSetRecoilState } from 'recoil';
import { useDropRegistrationVoi } from '~/hooks/registration/useDropRegistrationVoi';
import { fuseGridRow } from '~/hooks/gridFusion';
import { Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import type { Patient } from '~/domain/patient/patient';
import type { PatientRepository } from '~/domain/repository/patientRepository';
import {
    isRigidRegistration,
    RigidRegistration,
} from '~/domain/patient/rigidRegistration';
import type { ApprovableRegistration } from '~/domain/patient/approvableRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { ImportResult } from '~/domain/services/dicomProcessor';
import { DEFAULT_BLEND_SETTINGS } from '~/domain/viewport/fusionBlend';
import {
    DatasetSelection,
    selectDataset,
    SelectionRequest,
} from '~/domain/workflow/datasetSelection';
import { useSetCurrentDose } from '~/hooks/dose/useCurrentDose';
import { flushGridContourEdits } from '~/hooks/gridContourFlush';
import { readSelectedPatient } from '~/hooks/patient/useSelectedPatient';
import { localization } from '~/i18n';
import { currentDoseIdState } from '~/state/atoms/dose/doseAtoms';
import { optimizationObjectivesState } from '~/state/atoms/dose/optimizationObjectivesAtom';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    primaryImageIdState,
    secondaryImageIdState,
} from '~/state/atoms/image/imageAtoms';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import {
    RegistrationSelection,
    selectedRegistrationState,
} from '~/state/atoms/registration/registrationAtoms';
import {
    adjacentSliceGhostsEnabledState,
    roiPreview3DOpenState,
} from '~/state/atoms/viewerAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { useClearMultiSelection } from '~/hooks/structure-set/useClearMultiSelection';
import {
    currentStructureSetIdState,
    currentStructureSetState,
} from '~/state/atoms/structureSet/structureSetAtoms';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import { handleError } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    notifySelectionRefusal,
    notifyStructureSetNotShown,
} from '~/utils/notifySelectionRefusal';

/**
 * Picks a sensible default primary image when an import produces
 * more than one candidate. Preference order:
 *   1. CT modality over anything else (non-PT candidates only arrive
 *      here; CT beats MR etc.)
 *   2. Newest series date (missing dates rank lowest)
 *   3. Largest slice count (thickest stack wins ties)
 *
 * Assumes `candidates` is non-empty.
 */
function pickDefaultPrimaryImage(candidates: Image3D[]): Image3D {
    const score = (img: Image3D) => {
        const isCt = img.firstSlice.modality === Modality.CT ? 1 : 0;
        // ?? does not catch NaN, and an Invalid Date would make the comparator
        // return NaN, which leaves the sort order implementation-defined.
        const time = img.seriesMetadata.seriesDate?.getTime() ?? NaN;
        const dateMs = Number.isFinite(time) ? time : 0;
        const sliceCount = img.length;
        return { isCt, dateMs, sliceCount };
    };
    return [...candidates].sort((a, b) => {
        const sa = score(a);
        const sb = score(b);
        if (sa.isCt !== sb.isCt) {
            return sb.isCt - sa.isCt;
        }
        if (sa.dateMs !== sb.dateMs) {
            return sb.dateMs - sa.dateMs;
        }
        return sb.sliceCount - sa.sliceCount;
    })[0];
}

/** The images of `images` that `patient` holds, as the patient's instances. */
function imagesOfPatient(
    patient: Patient | null,
    images: Image3D[]
): Image3D[] {
    return images
        .map((img) => patient?.getImage3DWithDicomUid(img.dicomUid))
        .filter((img): img is Image3D => img !== undefined);
}

/** The first of `structureSets` that `patient` holds and that has an image. */
function displayableStructureSetOf(
    patient: Patient | null,
    structureSets: StructureSet[]
): StructureSet | undefined {
    return structureSets.find(
        (struct) =>
            struct.image !== null &&
            patient?.getStructureSetWithSopInstanceUid(struct.dicomUid)
    );
}

/** The first of `doses` that `patient` holds. */
function doseOfPatient(
    patient: Patient | null,
    doses: DoseMatrix[]
): DoseMatrix | undefined {
    return doses.find((dose) =>
        patient?.getDoseWithSopInstanceUid(dose.sopInstanceUid)
    );
}

/**
 * Whether `importResult` holds a dataset of `patient` that a selection step
 * takes as its candidate: an image, a structure set with an image, a dose.
 */
function holdsSelectionCandidate(
    patient: Patient | null,
    { images, structureSets, doses }: ImportResult
): boolean {
    return (
        imagesOfPatient(patient, images).length > 0 ||
        displayableStructureSetOf(patient, structureSets) !== undefined ||
        doseOfPatient(patient, doses) !== undefined
    );
}

/** The sidebar selection atoms, as ids. */
interface SelectionIds {
    readonly primaryImageId: string | null;
    readonly structureSetUid: string | undefined;
    readonly doseId: string | null;
    readonly secondaryImageId: string | null;
    readonly registration: RegistrationSelection | null;
}

// The registration a fused pair can show: a rigid one, or none.
function rigidOnly(
    registration: ApprovableRegistration | undefined
): RigidRegistration | undefined {
    return registration && isRigidRegistration(registration)
        ? registration
        : undefined;
}

/**
 * The datasets `ids` name in `patient`. An id that no longer resolves is
 * not selected.
 */
function resolveSelection(
    patient: Patient | null,
    ids: SelectionIds
): DatasetSelection {
    if (!patient) {
        return {};
    }
    const imageWithId = (id: string | null) =>
        id === null
            ? undefined
            : patient.images.find((image) => image.id === id);
    return {
        primaryImage: imageWithId(ids.primaryImageId),
        structureSet:
            ids.structureSetUid === undefined
                ? undefined
                : patient.getStructureSetWithSopInstanceUid(
                      ids.structureSetUid
                  ),
        dose:
            ids.doseId === null
                ? undefined
                : patient.doses.find((dose) => dose.id === ids.doseId),
        secondaryImage: imageWithId(ids.secondaryImageId),
        registration: rigidOnly(
            ids.registration === null
                ? undefined
                : patient.getRegistrationWithSopInstanceUid(
                      ids.registration.sopUid
                  )
        ),
    };
}

/** The registration selection the sidebar holds for `selection`. */
function registrationSelectionOf(
    selection: DatasetSelection
): RegistrationSelection | null {
    return selection.registration
        ? {
              sopUid: selection.registration.sopInstanceUid,
              imageSeriesUid: selection.secondaryImage?.seriesUid,
          }
        : null;
}

/** What a selection step decides from, as the Recoil store holds it. */
interface ViewerSelectionState {
    readonly engineStarted: boolean;
    readonly patientRepository: PatientRepository;
    readonly selectedPatientId: string | null;
    /** The sidebar selection, resolved in the selected patient. */
    readonly selection: DatasetSelection;
}

/**
 * Selects the datasets the viewer works on: the primary image, the
 * structure set, the dose and the fused pair. The selection rules are
 * {@link selectDataset}'s; this hook applies each outcome to the sidebar
 * atoms and to the grid registry, and tells the user about a refusal
 * before either moves.
 *
 * Every step decides from the selection the Recoil store holds when the
 * step is called, not when the calling component rendered: a step kept in
 * a callback that outlives the component that created it decides from the
 * selection of that moment. Each write reaches the store at once, so a step
 * called after another in the same tick builds on its writes. Inside the
 * body of a Recoil callback the store holds writes back until the body
 * returns; there, only the steps of `initializeEnvironment`, which pass
 * the selection from one to the next, build on each other.
 *
 * Until the engine has started, the selection steps do nothing.
 */
export default function useViewportEnvironment() {
    const readState = useRecoilCallback(
        ({ snapshot }) =>
            (): ViewerSelectionState => {
                const get = <T>(state: RecoilValue<T>): T =>
                    snapshot.getLoadable(state).getValue();
                return {
                    engineStarted: get(viewManagerState) !== null,
                    patientRepository: get(patientRepositoryState),
                    selectedPatientId: get(selectedPatientIdState),
                    selection: resolveSelection(readSelectedPatient(snapshot), {
                        primaryImageId: get(primaryImageIdState),
                        structureSetUid: get(currentStructureSetIdState),
                        doseId: get(currentDoseIdState),
                        secondaryImageId: get(secondaryImageIdState),
                        registration: get(selectedRegistrationState),
                    }),
                };
            },
        []
    );

    const setSelectedPatientId = useSetRecoilState(selectedPatientIdState);
    const setPrimaryImage = useSetPrimaryImage();
    const setSecondaryImage = useSetSecondaryImage();
    const setCurrentStructureSet = useSetRecoilState(currentStructureSetState);
    const setCurrentDose = useSetCurrentDose();
    const setOptimizationObjectives = useSetRecoilState(
        optimizationObjectivesState
    );
    const setSelectedRegistration = useSetRecoilState(
        selectedRegistrationState
    );
    const setSelectedROI = useSetRecoilState<number>(selectedROIState);
    const clearMultiSelection = useClearMultiSelection();
    const setRoiPreviewOpen = useSetRecoilState(roiPreview3DOpenState);
    const setAdjacentSliceGhostsEnabled = useSetRecoilState(
        adjacentSliceGhostsEnabledState
    );
    const setTool = useSetRecoilState(selectedToolState);
    const dropRegistrationVoi = useDropRegistrationVoi();
    const searchPatient = (
        patientRepository: PatientRepository,
        patientId?: string
    ) => {
        if (patientId == null) {
            return;
        }
        const patientToOpen = patientRepository
            .getAll()
            .find((patient) => patient.dataSet.PatientID === patientId);
        return patientToOpen;
    };

    const setStructureSet = (structureSet: StructureSet | undefined) => {
        if (structureSet) {
            setCurrentStructureSet(createStructureSetReadModel(structureSet));
        } else {
            setCurrentStructureSet(undefined);
        }
    };

    /** Writes every field of `selection` to the sidebar atoms. */
    const applySelection = (selection: DatasetSelection) => {
        setPrimaryImage(selection.primaryImage);
        setStructureSet(selection.structureSet);
        setSecondaryImage(selection.secondaryImage);
        setCurrentDose(selection.dose);
        setSelectedRegistration(registrationSelectionOf(selection));
    };

    /**
     * The outcome of `request` on `current`. A refusal is told to the user
     * here, before anything moves.
     */
    const decide = (current: DatasetSelection, request: SelectionRequest) => {
        const outcome = selectDataset(current, request);
        if (outcome.kind === 'refused') {
            notifySelectionRefusal(outcome.refusal);
        }
        return outcome;
    };

    /**
     * Selects the first of `doses` that belongs to the patient, with the
     * structure set it references, from `state`. Returns the selection
     * afterwards, which is `state`'s when nothing moved.
     */
    const selectDose = (
        state: ViewerSelectionState,
        doses: DoseMatrix[],
        patientId: string
    ): DatasetSelection => {
        const dose = doseOfPatient(
            state.patientRepository.getByPatientId(patientId),
            doses
        );
        if (!state.engineStarted || !dose) {
            return state.selection;
        }
        const outcome = decide(state.selection, { kind: 'dose', dose });
        if (outcome.kind !== 'selected' || !dose.structureSet) {
            return state.selection;
        }
        // A dose is drawn with the structure set it references, so that
        // set becomes the active one first, in the sidebar and on the grid,
        // and the dose follows it. A refused switch loads no dose: the dose
        // would take the set with it and discard the grid's unsaved edits
        // of the set that left.
        if (
            outcome.structureSetChanged &&
            !activateStructureSet(
                dose.structureSet,
                { ...outcome.selection, dose: undefined },
                patientId,
                localization.t('contouring.gridSaveFailed.doseNotLoaded')
            )
        ) {
            return state.selection;
        }
        setSelectedPatientId(patientId);
        applySelection(outcome.selection);
        setOptimizationObjectives(dose.optimizationObjectives ?? []);
        const shown = getDatasetEnvironmentRegistry().setActiveDose(dose);
        // A switch to a set whose image is not loaded has said so
        // already, and a dose of that set has no row either; one
        // warning is enough.
        if (!shown && !outcome.structureSetChanged) {
            enqueueSnackbar(localization.t('dose.notShown.imageNotLoaded'), {
                variant: 'warning',
            });
        }
        return outcome.selection;
    };

    const setEnvironmentDose = (doses: DoseMatrix[], patientId: string) => {
        selectDose(readState(), doses, patientId);
    };

    /**
     * Clears the registry's active dose. Once the engine has started it
     * also deselects the dose and empties the optimization objectives.
     */
    const unloadDose = () => {
        getDatasetEnvironmentRegistry().setActiveDose(undefined);
        if (readState().engineStarted) {
            setCurrentDose(undefined);
            setOptimizationObjectives([]);
        }
    };

    /**
     * Activates the first displayable set of `structureSets` from `state`.
     * Returns the selection afterwards, or undefined when that set is not
     * active afterwards: none is displayable or the switch was refused.
     */
    const selectStructureSet = (
        state: ViewerSelectionState,
        structureSets: StructureSet[],
        patientId: string
    ): DatasetSelection | undefined => {
        // A set with no image cannot be displayed: the grid draws it on
        // the image it carries. So it is not a candidate to auto-select.
        // Offering one anyway would report "not connected to an image" for
        // a dataset nobody chose: this list is every loaded set whenever
        // the caller expressed no preference.
        const structureSet = displayableStructureSetOf(
            state.patientRepository.getByPatientId(patientId),
            structureSets
        );
        if (!state.engineStarted || !structureSet) {
            return undefined;
        }
        const outcome = decide(state.selection, {
            kind: 'structureSet',
            structureSet,
        });
        if (
            outcome.kind !== 'selected' ||
            !activateStructureSet(
                structureSet,
                outcome.selection,
                patientId,
                localization.t(
                    'contouring.gridSaveFailed.structureSetNotSwitched'
                )
            )
        ) {
            return undefined;
        }
        return outcome.selection;
    };

    /**
     * Activates the first displayable set of `structureSets`. Returns whether
     * that set is active afterwards: false when none is displayable or the
     * switch was refused.
     */
    const setEnvironmentStructureSet = (
        structureSets: StructureSet[],
        patientId: string
    ): boolean =>
        selectStructureSet(readState(), structureSets, patientId) !== undefined;

    /**
     * Applies `selection`, whose structure set is `structureSet`, and makes
     * that set the grid's active set. The grid's unsaved contour edits of
     * the leaving set are saved first: moving the set frees the SDF
     * textures they live in. Returns false when nothing moved because
     * those edits could not be saved; `flushConsequence` is what the user
     * is told follows a failed save.
     */
    const activateStructureSet = (
        structureSet: StructureSet,
        selection: DatasetSelection,
        patientId: string,
        flushConsequence: string
    ): boolean => {
        const registry = getDatasetEnvironmentRegistry();
        if (!flushGridContourEdits(registry, flushConsequence)) {
            return false;
        }
        cleanupStructureSetContext();
        setSelectedPatientId(patientId);
        applySelection(selection);
        // A set whose image the grid does not hold leaves the grid empty
        // and its tools disarmed; say so instead of leaving the user to
        // wonder why nothing draws.
        if (!registry.setActiveStructureSet(structureSet)) {
            notifyStructureSetNotShown();
        }
        return true;
    };

    /**
     * Takes the selected structure set off the sidebar and off the grid, so
     * no tile goes on drawing it.
     */
    const unloadStructureSet = () => {
        if (readState().engineStarted) {
            cleanupStructureSetContext();
            getDatasetEnvironmentRegistry().setActiveStructureSet(undefined);
        }
    };

    const cleanupStructureSetContext = () => {
        setStructureSet(undefined);
        setSelectedROI(-1);
        clearMultiSelection();
        setRoiPreviewOpen(false);
        setAdjacentSliceGhostsEnabled(false);
        setTool('pan');
        unloadDose();
    };

    /**
     * Makes the preferred of `images` the primary image, from `state`.
     * Returns the selection afterwards, or undefined when it is not the
     * primary afterwards: none of them belongs to the patient or the switch
     * was refused. The current primary asked for again changes nothing.
     */
    const selectPrimaryImage = (
        state: ViewerSelectionState,
        images: Image3D[],
        patientId: string
    ): DatasetSelection | undefined => {
        // Resolve each incoming image to the aggregate's own instance rather
        // than trusting the caller's: it is the resolved instance's `id` that
        // lands in the primary image atom, so an argument that merely shares
        // a UID with an aggregate member must not flow through as the
        // selected image.
        const candidates = imagesOfPatient(
            state.patientRepository.getByPatientId(patientId),
            images
        );
        if (candidates.length === 0 || !state.engineStarted) {
            return undefined;
        }
        // Auto-select a sensible default so tabs that depend on a
        // primary image light up immediately after import. Prefer CT
        // over other modalities, then the newest series, then the
        // one with the most slices. The user can still switch later
        // via the image list.
        const image = pickDefaultPrimaryImage(candidates);
        const outcome = decide(state.selection, {
            kind: 'primaryImage',
            image,
        });
        if (outcome.kind === 'unchanged') {
            return state.selection;
        }
        if (outcome.kind !== 'selected') {
            return undefined;
        }
        // A new primary image comes with no structure set and no dose, so
        // the grid lets go of them too: rows of the old image must not keep
        // drawing a set the sidebar no longer names. The set's unsaved grid
        // edits are saved first, or nothing switches.
        const registry = getDatasetEnvironmentRegistry();
        if (
            !flushGridContourEdits(
                registry,
                localization.t('contouring.gridSaveFailed.imageNotSwitched')
            )
        ) {
            return undefined;
        }
        setSelectedPatientId(patientId);
        applySelection(outcome.selection);
        registry.setActiveStructureSet(undefined);
        setTool('pan');
        return outcome.selection;
    };

    /**
     * Makes the preferred of `images` the primary image. Returns whether it
     * is the primary afterwards: false when none of them belongs to the
     * patient or the switch was refused. The current primary asked for
     * again changes nothing.
     */
    const setEnvironmentPrimaryImage = (
        images: Image3D[],
        patientId: string
    ): boolean =>
        selectPrimaryImage(readState(), images, patientId) !== undefined;

    const removePrimaryImage = () => {
        const state = readState();
        if (!state.engineStarted) {
            return;
        }
        // As on a switch, the grid lets go of the set with the image, and
        // the set's unsaved grid edits are saved first, or nothing unloads.
        const registry = getDatasetEnvironmentRegistry();
        if (
            !flushGridContourEdits(
                registry,
                localization.t('contouring.gridSaveFailed.imageNotUnloaded')
            )
        ) {
            return;
        }
        // The unloaded image lets go of its pair too, so picking it again
        // starts unfused at the default blend. A row with no pair keeps its
        // blend: a lone PET row is drawn through its ramp.
        const primary = state.selection.primaryImage;
        if (primary && registry.entryFor(primary.seriesUid)?.secondaryImage) {
            registry.clearFusion(primary.seriesUid);
        }
        setPrimaryImage(undefined);
        removeEnvironmentSecondaryImage();
        cleanupStructureSetContext();
        registry.setActiveStructureSet(undefined);
    };

    /**
     * Fuses `secondary` onto the grid row of the primary image of
     * `selection` through `registration`, when the grid's
     * frame-of-reference gate admits the pair; a refused pair is told to
     * the user by that gate. Only a fused pair becomes the sidebar's
     * secondary image and registration, drops the registration box and is
     * shown at an even blend.
     */
    const fuseOntoPrimary = (
        selection: DatasetSelection,
        secondary: Image3D,
        registration: RigidRegistration | undefined,
        patient: Patient | undefined
    ) => {
        const primary = selection.primaryImage;
        if (!primary) {
            return;
        }
        const registry = getDatasetEnvironmentRegistry();
        if (!fuseGridRow(registry, patient, primary, secondary, registration)) {
            return;
        }
        applySelection({
            ...selection,
            secondaryImage: secondary,
            registration,
        });
        dropRegistrationVoi();
        registry.setBlend(primary.seriesUid, {
            alpha: DEFAULT_BLEND_SETTINGS.alpha,
        });
    };

    const removeEnvironmentSecondaryImage = () => {
        if (!readState().engineStarted) {
            return;
        }
        setSecondaryImage(undefined);
        setSelectedRegistration(null);
        setTool('pan');
    };

    const resolvePatient = (
        state: ViewerSelectionState,
        importResult: ImportResult
    ) => {
        try {
            let patient;
            if (importResult.images[0]) {
                const sliceData = importResult.images[0]?.firstSlice;
                patient = searchPatient(
                    state.patientRepository,
                    sliceData.datasetWrapper.PatientID
                );
            } else if (importResult.structureSets[0]) {
                patient = searchPatient(
                    state.patientRepository,
                    importResult.structureSets[0].dicomStructureSet
                        .datasetWrapper.PatientID
                );
            } else if (importResult.doses[0]) {
                patient = searchPatient(
                    state.patientRepository,
                    // A dose names its own patient; it need not reference a set.
                    importResult.doses[0].structureSet?.dicomStructureSet
                        ?.datasetWrapper.PatientID ??
                        importResult.doses[0].dicomDose.datasetWrapper.PatientID
                );
            }

            return patient
                ? patient.dataSet.PatientID
                : state.selectedPatientId;
        } catch (error) {
            handleError(
                error,
                'Error when trying to resolve patient data from imported object.'
            );
        }
    };

    /**
     * Objects the caller wants selected, rather than the defaults this picks.
     * Used by the worklist open, which resolved the choice on the list and
     * showed it to the user before they committed.
     */
    type PreferredObjects = {
        image?: Image3D;
        /** Registration only: the other side of the pair. */
        secondaryImage?: Image3D;
        structureSet?: StructureSet;
    };

    /**
     * Selects the datasets of `importResult`: the primary image, then a
     * structure set, then a dose, then the preferred pair. Each step decides
     * from the selection the step before it left, and selects the import's
     * patient when it succeeds. When no step succeeds, the import's patient
     * is selected only if the sidebar named no primary image: a primary
     * image on screen keeps its patient, so a refused step never leaves the
     * sidebar naming a patient the grid does not show. When it keeps its
     * patient for another patient's import that holds nothing to select,
     * the user is warned that the import is not shown. Resolves to the
     * primary image once all of that is applied.
     */
    const initializeEnvironment = async (
        importResult: ImportResult,
        prefer: PreferredObjects = {}
    ): Promise<Image3D | undefined> => {
        const { images, doses, structureSets, patients, registrations } =
            importResult;
        const state = readState();
        let selection = state.selection;
        const patientId = patients[0]
            ? importResult.patients[0].PatientID
            : resolvePatient(state, importResult);
        if (patientId != null) {
            // intentional != to catch both null and undefined
            // A preference narrows the candidates rather than bypassing the
            // setters: they own the selection's side effects (tool reset,
            // object ids), and a second path through them is a second place
            // for those to drift.
            const afterPrimary =
                images.length > 0
                    ? selectPrimaryImage(
                          state,
                          prefer.image ? [prefer.image] : images,
                          patientId
                      )
                    : undefined;
            selection = afterPrimary ?? selection;
            if (structureSets.length > 0) {
                selection =
                    selectStructureSet(
                        { ...state, selection },
                        prefer.structureSet
                            ? [prefer.structureSet]
                            : structureSets,
                        patientId
                    ) ?? selection;
            }
            if (doses.length > 0) {
                selection = selectDose(
                    { ...state, selection },
                    doses,
                    patientId
                );
            }
            // With no primary image on screen there is no other patient to
            // keep, so an import that had nothing to select, such as a
            // structure set whose image is not loaded, still opens its
            // patient. A step that succeeded selected this patient already.
            // Another patient is another grid, whose rows let go of the
            // Environments the grid's unsaved contour edits live in, so the
            // edits are saved first.
            if (state.selection.primaryImage === undefined) {
                if (patientId !== state.selectedPatientId) {
                    flushGridContourEdits(
                        getDatasetEnvironmentRegistry(),
                        localization.t(
                            'contouring.gridSaveFailed.editsMayBeLost'
                        )
                    );
                }
                setSelectedPatientId(patientId);
            } else if (
                patientId !== state.selectedPatientId &&
                !holdsSelectionCandidate(
                    state.patientRepository.getByPatientId(patientId),
                    importResult
                )
            ) {
                // Nothing on screen changes for such an import, so the user
                // is told; a step with a candidate shows it or says why not.
                enqueueSnackbar(localization.t('importOtherPatientNotShown'), {
                    variant: 'warning',
                });
            }
            // Last, and only when the caller named one: it takes the import's
            // first registration from the preferred primary's frame to the
            // secondary's, which is the whole point of landing in the
            // registration step with a pair already on screen. The pair
            // belongs to the preferred primary: when that image is not the
            // primary here, because its switch was refused or the structure
            // set took the primary to its own image, nothing is fused.
            const primary = selection.primaryImage;
            const preferredIsPrimary =
                afterPrimary !== undefined &&
                primary !== undefined &&
                (prefer.image === undefined ||
                    primary.seriesUid === prefer.image.seriesUid);
            if (prefer.secondaryImage && preferredIsPrimary) {
                const patient =
                    state.patientRepository.getByPatientId(patientId) ??
                    undefined;
                const fromPrimary = prefer.image
                    ? patient?.findRegistrationsFrom(
                          prefer.image,
                          prefer.secondaryImage
                      )
                    : undefined;
                fuseOntoPrimary(
                    selection,
                    prefer.secondaryImage,
                    registrations.find((registration) =>
                        fromPrimary?.includes(registration)
                    ),
                    patient
                );
            }
        }
        return selection.primaryImage;
    };

    return {
        initializeEnvironment,
        setEnvironmentDose,
        unloadDose,
        setEnvironmentStructureSet,
        unloadStructureSet,
        setEnvironmentPrimaryImage,
        removePrimaryImage,
        removeEnvironmentSecondaryImage,
    };
}
